import React, { useEffect } from 'react';

import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import SEO, { schemas } from '../components/SEO';
import './InnerPage.css';

const owaspTiles = [
  { id:'LLM01', name:'Prompt Injection',            icon:'/icons/security/security_31_llm01-prompt-injection.svg', count:'22 tests', sev:'Critical', active:true,  desc:'Tests direct and indirect prompt injection including instruction override, role hijacking, and system prompt extraction.' },
  { id:'LLM02', name:'Insecure Output Handling',    icon:'/icons/security/security_32_llm02-insecure-output.svg',  count:'18 tests', sev:'High',     active:true,  desc:'Evaluates whether AI outputs can trigger downstream execution, XSS, or injection in consuming applications.' },
  { id:'LLM03', name:'Training Data Poisoning',     icon:'/icons/security/security_33_llm03-data-poisoning.svg',   count:'15 tests', sev:'High',     active:true,  desc:'Tests resistance to queries that attempt to surface poisoned knowledge or expose training artifacts.' },
  { id:'LLM04', name:'Model Denial of Service',     icon:'/icons/security/security_34_llm04-denial-of-service.svg',count:'4 tests',  sev:'Medium',   active:true,  desc:'Evaluates model behaviour under resource-exhausting inputs and recursive prompt patterns.' },
  { id:'LLM05', name:'Supply Chain Vulnerabilities',icon:'/icons/security/security_35_llm05-supply-chain.svg',     count:'4 tests',  sev:'Medium',   active:true,  desc:'Tests for risks from third-party plugins, model dependencies, and unsafe tool integrations.' },
  { id:'LLM06', name:'Sensitive Info Disclosure',   icon:'/icons/security/security_36_llm06-info-disclosure.svg',  count:'14 tests', sev:'High',     active:true,  desc:'Probes for leakage of PII, credentials, internal system details, and confidential business data.' },
  { id:'LLM07', name:'Insecure Plugin Design',      icon:'/icons/security/security_37_llm07-plugin-design.svg',    count:'37 tests', sev:'High',     active:true,  desc:'Evaluates plugin and tool-use security including over-privileged function calls and SSRF vectors.' },
  { id:'LLM08', name:'Excessive Agency',            icon:'/icons/security/security_38_llm08-excessive-agency.svg', count:'4 tests',  sev:'Medium',   active:true,  desc:'Tests whether the model takes unauthorised autonomous actions or exceeds its defined scope.' },
  { id:'LLM09', name:'Overreliance',                icon:'/icons/security/security_39_llm09-overreliance.svg',     count:'3 tests',  sev:'Low',      active:true,  desc:'Evaluates whether the model presents hallucinated content with inappropriate confidence.' },
  { id:'LLM10', name:'Model Theft',                 icon:'/icons/security/security_40_llm10-model-theft.svg',      count:'Roadmap',  sev:'Roadmap',  active:false, desc:'Testing for model extraction, inversion attacks, and intellectual property leakage. Coming soon.' },
];

const sevColor = { Critical:'#dc2626', High:'#ea580c', Medium:'#d97706', Low:'#4361ee', Roadmap:'#9ca3af' };
const sevBg    = { Critical:'#fee2e2', High:'#fff7ed', Medium:'#fef3c7', Low:'#e8ecff',  Roadmap:'#f3f4f6' };

export default function SecurityPage() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); obs.unobserve(e.target); } }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="Security"
        description="Accugen covers 9 of 10 OWASP LLM Top 10 categories with 122+ security tests across 14 attack vectors. Enterprise AI security testing for prompt injection, jailbreaking, RAG poisoning and more."
        canonical="/security"
        keywords="OWASP LLM Top 10, AI security testing, prompt injection testing, jailbreak detection, RAG poisoning, LLM vulnerability scanner"
        schema={schemas.breadcrumb([{name:'Home',path:'/'},{name:'Security',path:'/security'}])}
      />
      <Header />

      <PageHero
        eyebrow="AI Security"
        title="Partnership with<br/><em>OWASP LLM Top 10</em>"
        subtitle="Accugen maps every test to the OWASP LLM Top 10 framework — the industry standard for identifying and mitigating risks in large language model applications."
        crumb="Security"
      />

      {/* Stats row */}
      <section className="ip-section ip-bg-lav" style={{paddingTop:56,paddingBottom:56}}>
        <div className="container">
          <div className="ip-stats-row">
            {[{n:'9/10',l:'OWASP Categories'},{n:'122+',l:'Total Tests'},{n:'14',l:'Attack Vectors'},{n:'5',l:'Severity Levels'}].map(s => (
              <div key={s.l} className="ip-stat-box">
                <div className="ip-stat-num">{s.n}</div>
                <div className="ip-stat-lbl">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OWASP detail cards */}
      <section className="ip-section">
        <div className="container">
          <div className="eyebrow reveal" style={{textAlign:'center'}}>Coverage Details</div>
          <h2 className="section-h reveal d1" style={{textAlign:'center'}}>OWASP LLM Top 10<br/><em>Full Coverage Map</em></h2>
          <div className="sec-owasp-grid">
            {owaspTiles.map((t, i) => (
              <div key={t.id} className={`sec-owasp-card${!t.active ? ' sec-roadmap' : ''} reveal d${(i % 4) + 1}`}>
                <div className="sec-owasp-top">
                  <div className="sec-owasp-emoji"><img src={t.icon} alt={t.name} /></div>
                  <span className="sec-sev-pill" style={{background: sevBg[t.sev], color: sevColor[t.sev]}}>{t.sev}</span>
                </div>
                <div className="sec-owasp-id">{t.id}</div>
                <div className="sec-owasp-name">{t.name}</div>
                <p className="sec-owasp-desc">{t.desc}</p>
                <div className="sec-owasp-count">{t.active ? `✓ ${t.count}` : t.count}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attack categories */}
      <section className="ip-section ip-bg-lav">
        <div className="container">
          <div className="eyebrow reveal" style={{textAlign:'center'}}>Attack Categories</div>
          <h2 className="section-h reveal d1" style={{textAlign:'center'}}>14 Attack Vectors<br/><em>Systematically Tested</em></h2>
          <div className="ip-card-grid3 reveal d2">
            {[
              {icon:'/icons/security/attack_01_prompt-injection.svg', t:'Prompt Injection',        d:'Direct and indirect injection, instruction override, role hijacking'},
              {icon:'/icons/security/attack_02_jailbreaking.svg', t:'Jailbreaking',             d:'DAN variants, roleplay exploits, multi-turn instruction erosion'},
              {icon:'/icons/security/attack_03_data-leakage.svg', t:'Data Leakage',             d:'PII extraction, credential surfacing, context window exfiltration'},
              {icon:'/icons/security/attack_04_rag-poisoning.svg', t:'RAG Poisoning',            d:'Retrieval manipulation, document injection, context corruption'},
              {icon:'/icons/security/attack_11_social-engineering.svg', t:'Social Engineering',       d:'Authority exploitation, urgency framing, trust manipulation'},
              {icon:'/icons/security/attack_05_adversarial-formatting.svg', t:'Adversarial Formatting',   d:'Encoding tricks, invisible characters, structured format abuse'},
              {icon:'/icons/security/attack_10_context-overflow.svg', t:'Cross-context Attacks',    d:'Memory injection, cross-session state manipulation'},
              {icon:'/icons/security/attack_08_insecure-plugin.svg', t:'Plugin Exploitation',      d:'Over-privileged tools, SSRF, unsafe function call chaining'},
              {icon:'/icons/security/attack_07_indirect-injection.svg', t:'Autonomous Action',        d:'Scope boundary testing, multi-step agentic attack chains'},
              {icon:'/icons/security/attack_09_sensitive-disclosure.svg', t:'Hallucination Probing',    d:'False confidence induction, fabrication under pressure'},
              {icon:'/icons/security/attack_06_model-extraction.svg', t:'System Prompt Extraction', d:'Leakage probes, boundary confusion, indirect disclosure'},
              {icon:'/icons/security/attack_13_denial-of-service.svg', t:'Resource Exhaustion',      d:'Token flooding, recursive expansion, context overflow'},
              {icon:'/icons/security/attack_12_training-data-poisoning.svg', t:'Training Artifact Probe', d:'Verbatim memorisation, dataset inference attacks'},
              {icon:'/icons/security/attack_14_supply-chain.svg', t:'Supply Chain Risks',       d:'Third-party model risks, plugin dependency attacks'},
            ].map(c => (
              <div key={c.t} className="ip-card">
                <div className="ip-card-icon"><img src={c.icon} alt={c.name} /></div>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ip-cta-band">
        <div className="container">
          <h2>Start Your Security Audit Today</h2>
          <p>Run your first OWASP LLM Top 10 evaluation in minutes.</p>
          <div className="ip-cta-btns">
            <a href="/contact" className="btn-navy" target="_blank" rel="noreferrer">Contact Us →</a>
            <Link to="/contact" className="btn-outline-navy">Talk to an Expert</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
